import type { ReactElement } from 'react'
import { Alert, Box, Button, CircularProgress, List, Typography } from '@mui/material'
import type { UserNotification } from '../../api/contracts'
import type { NotificationsFeedState } from '../../hooks/useNotificationsFeed'
import NotificationFilterChips from './NotificationFilterChips'
import NotificationListItem from './NotificationListItem'
import { resolveNotificationPath } from './notificationUrl'
import { NOTIFICATIONS_TEXT } from './notificationsText'

interface NotificationsFeedPanelProps {
  feed: NotificationsFeedState
  unreadCount: number
  onOpenNotification: (notification: UserNotification) => void
}

const LINE = '1px solid #e6eaf1'

/** Filters, rows and «Показать ещё» shared by the desktop drawer and the phone sheet. */
export default function NotificationsFeedPanel({
  feed,
  unreadCount,
  onOpenNotification
}: NotificationsFeedPanelProps): ReactElement {
  const now = new Date()
  const isEmpty = feed.status === 'ready' && feed.items.length === 0

  return (
    <>
      <Box
        sx={{
          flexShrink: 0,
          px: 2,
          py: '10px',
          borderBottom: LINE,
          overflowX: 'auto',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' }
        }}
      >
        <NotificationFilterChips
          value={feed.filter}
          onChange={feed.setFilter}
          allCount={feed.allCount}
          unreadCount={unreadCount}
        />
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {feed.actionErrorMessage && (
          <Alert severity="error" sx={{ m: 2 }}>{feed.actionErrorMessage}</Alert>
        )}

        {feed.status === 'loading' && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} aria-label={NOTIFICATIONS_TEXT.loading} />
          </Box>
        )}

        {feed.status === 'error' && (
          <Alert
            severity="error"
            sx={{ m: 2 }}
            action={(
              <Button color="inherit" size="small" onClick={feed.retry}>
                {NOTIFICATIONS_TEXT.retry}
              </Button>
            )}
          >
            {feed.errorMessage}
          </Alert>
        )}

        {isEmpty && (
          <Typography sx={{ px: 2, py: 5, textAlign: 'center', fontSize: 13.5, color: '#6b7482' }}>
            {feed.filter === 'all' ? NOTIFICATIONS_TEXT.emptyAll : NOTIFICATIONS_TEXT.emptyFiltered}
          </Typography>
        )}

        {feed.items.length > 0 && (
          <List disablePadding aria-label={NOTIFICATIONS_TEXT.title}>
            {feed.items.map((notification) => (
              <NotificationListItem
                key={notification.id}
                notification={notification}
                now={now}
                onClick={resolveNotificationPath(notification) ? () => onOpenNotification(notification) : undefined}
              />
            ))}
          </List>
        )}

        {feed.hasMore && (
          <Box sx={{ px: 2, py: '12px', textAlign: 'center', borderTop: LINE }}>
            <Button
              variant="text"
              onClick={feed.loadMore}
              disabled={feed.isLoadingMore}
              sx={{ textTransform: 'none', fontSize: 13, fontWeight: 600, color: '#1a56c4' }}
            >
              {NOTIFICATIONS_TEXT.showMore}
            </Button>
          </Box>
        )}
      </Box>
    </>
  )
}
